import {
  Check,
  DoorOpen,
  LogOut,
  Headphones,
  MessageCircle,
  Pause,
  Play,
  RotateCcw,
  Settings2,
  ShieldCheck,
  SkipForward,
  Skull,
  Sparkles,
  Trash2,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PHASE_COPY,
  ROLE_LABELS,
  type ChatChannel,
  type GamePhase,
  type PrivatePlayer,
  type PublicPlayer,
  type Role,
  type RoomState,
  type VoiceParticipant,
} from "@ma-soi/game-core";
import {
  apiRequest,
  callRoomCommand,
  errorMessage,
  getResumeToken,
  removeResumeToken,
  requestId,
  saveResumeToken,
} from "../lib";
import { useRoomConnection } from "../hooks/useRoomConnection";
import { useVoiceChat } from "../hooks/useVoiceChat";
import { useGlobalSound } from "../hooks/SoundContext";
import { ChatPanel } from "../components/ChatPanel";
import {
  AnimatedLandingBackground,
  EmptySeatCard,
  GameButton,
  InviteScroll,
  LobbyCouncilBoard,
  SurfacePanel,
  PlayerLobbyCard,
  RoomSeal,
  SettingsToggle,
} from "../components/GameUi";
import { HostVoiceControls } from "../components/HostVoiceControls";
import { PlayerGrid } from "../components/PlayerGrid";
import { Timer } from "../components/Timer";
import { VoicePanel, VoiceAudioSink } from "../components/VoicePanel";
import { MobileGameDock, type MobileSheet } from "../components/game/MobileGameDock";
import { RoleReveal } from "../components/game/RoleReveal";
import { ActionPanel } from "../components/game/ActionPanel";
import { ClawMark, PhaseSigil } from "../components/game/PhaseArt";
import { usePhaseVfx } from "../hooks/usePhaseVfx";
import { useVisualEffects } from "../hooks/VisualEffectsContext";
import { VoteResultView } from "../components/game/VoteResultView";
import { BottomSheet } from "../components/ui/BottomSheet";
import { AvatarPicker } from "../components/AvatarPicker";
import type { AudioScene, SfxId } from "../audio/catalog";

function sceneForPhase(phase: GamePhase, winner: RoomState["winner"]): AudioScene {
  switch (phase) {
    case "LOBBY": return "lobby";
    case "ROLE_REVEAL": return "role_reveal";
    case "NIGHT_GUARD":
    case "NIGHT_WOLVES":
    case "NIGHT_SEER":
    case "NIGHT_WITCH":
    case "NIGHT_RESOLUTION":
    case "HUNTER_REACTION":
      return "night";
    case "MORNING": return "morning";
    case "DISCUSSION": return "discussion";
    case "VOTING": return "voting";
    case "VOTE_RESULT": return "voting";
    case "ENDED": return winner === "VILLAGERS"
      ? "ended_villagers"
      : winner === "WEREWOLVES" ? "ended_wolves" : "discussion";
  }
}

function phaseSfx(phase: GamePhase, winner: RoomState["winner"], deathCount: number): SfxId | null {
  switch (phase) {
    case "ROLE_REVEAL": return null;
    case "NIGHT_GUARD": return "role_guard";
    case "NIGHT_WOLVES": return "role_wolf";
    case "NIGHT_SEER": return "role_seer";
    case "NIGHT_WITCH": return "role_witch";
    case "HUNTER_REACTION": return "phase_change";
    case "MORNING": return deathCount > 0 ? "morning_death" : "morning_safe";
    case "DISCUSSION":
    case "VOTING":
      return "phase_change";
    case "VOTE_RESULT":
      return null;
    case "ENDED": return winner === "VILLAGERS"
      ? "villagers_win"
      : winner === "WEREWOLVES" ? "wolves_win" : null;
    default: return null;
  }
}

const ROLE_ONLY_AUDIO_PHASES: Partial<Record<GamePhase, Role>> = {
  NIGHT_GUARD: "GUARD",
  NIGHT_WOLVES: "WEREWOLF",
  NIGHT_SEER: "SEER",
  NIGHT_WITCH: "WITCH",
  HUNTER_REACTION: "HUNTER",
};

export function RoomPage() {
  const { roomCode = "" } = useParams();
  const roomId = roomCode.toUpperCase();
  const navigate = useNavigate();
  const sound = useGlobalSound();
  const connection = useRoomConnection(roomId);
  const room = connection.snapshot?.room ?? null;
  const players = connection.snapshot?.players ?? [];
  const privatePlayer = connection.snapshot?.privatePlayer ?? null;
  const events = connection.snapshot?.events ?? [];
  const messages = connection.snapshot?.messages ?? [];
  const userId = connection.snapshot?.uid ?? null;
  const voiceSnapshot = connection.snapshot?.voice ?? null;
  const visual = usePhaseVfx(connection.snapshot, connection.connected);
  const { setBackdrop } = useVisualEffects();
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [joinName, setJoinName] = useState(() => localStorage.getItem("ma-soi:name") ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showHost, setShowHost] = useState(false);
  const [showLobbySettings, setShowLobbySettings] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [mobileSheet, setMobileSheet] = useState<MobileSheet | null>(null);
  const previousLobbyRef = useRef<{
    ids: Set<string>;
    ready: Map<string, boolean>;
    allReady: boolean;
  } | null>(null);
  const playedEventAudioRef = useRef(new Set<string>());

  const currentPlayer = players.find((player) => player.uid === userId) ?? null;
  const isHost = room?.hostId === userId;
  const voiceChat = useVoiceChat({
    roomId,
    userId,
    players,
    voice: voiceSnapshot,
    socketReady: connection.connected,
    sendSocketMessage: connection.sendSocketMessage,
    voiceSignal: connection.voiceSignal,
  });
  const voiceParticipants = voiceSnapshot?.participants ?? [];
  const voiceByPlayer = useMemo(() => new Map(voiceParticipants.map((participant) => [participant.playerId, participant])), [voiceParticipants]);
  const channel = useMemo<ChatChannel>(() => connection.snapshot?.channel ?? "lobby", [
    connection.snapshot?.channel,
  ]);

  useEffect(() => {
    setSelectedTarget(null);
    setMobileSheet(null);
    setShowLobbySettings(false);
    setShowAvatarPicker(false);
  }, [roomId, room?.gameId, room?.phaseId]);

  useEffect(() => {
    setBackdrop(room?.phase === "MORNING" || room?.phase === "DISCUSSION" || (room?.phase === "ENDED" && room.winner === "VILLAGERS") ? "dawn" : "night");
    return () => setBackdrop("night");
  }, [room?.phase, room?.winner, setBackdrop]);

  useEffect(() => {
    if (!connection.roomClosed) return;
    removeResumeToken(roomId);
    navigate("/", {
      replace: true,
      state: { notice: connection.roomClosed },
    });
  }, [connection.roomClosed, navigate, roomId]);

  useEffect(() => {
    if (!room) return;
    const key = `${roomId}:${room.gameId ?? "lobby"}:${room.phaseId}`;
    sound.setScene(sceneForPhase(room.phase, room.winner), key);
    const cue = phaseSfx(room.phase, room.winner, room.lastDeaths.length);
    const requiredRole = ROLE_ONLY_AUDIO_PHASES[room.phase];
    const roleCueEligible = privatePlayer?.role === requiredRole
      && (room.phase === "HUNTER_REACTION" || currentPlayer?.alive);
    if (cue && (!requiredRole || roleCueEligible)) {
      sound.playSfx(cue, `${key}:phase`);
    }
  }, [currentPlayer?.alive, privatePlayer?.role, room?.gameId, room?.lastDeaths.length, room?.phase, room?.phaseId, room?.winner, roomId, sound.playSfx, sound.setScene]);

  useEffect(() => {
    for (const event of events) {
      if (playedEventAudioRef.current.has(event.id) || Date.now() - event.createdAtMs > 10_000) continue;
      let cue: SfxId | null = null;
      if (event.type === "vote_elimination") {
        cue = "elimination";
      } else if (event.type === "vote_tie") {
        cue = "vote_tie";
      } else if (event.type === "hunter_shot") {
        cue = "hunter_shot";
      } else if (event.type === "hunter_skipped") {
        cue = "hunter_skip";
      } else if (event.type === "game_started") {
        cue = "game_start";
      }
      if (!cue) continue;
      playedEventAudioRef.current.add(event.id);
      sound.playSfx(cue, `${roomId}:event:${event.id}`);
    }
  }, [events, roomId, sound.playSfx]);

  useEffect(() => {
    if (room?.status !== "lobby") {
      previousLobbyRef.current = null;
      return;
    }

    const ids = new Set(players.map((player) => player.uid));
    const ready = new Map(players.map((player) => [player.uid, player.ready]));
    const allReady = players.length >= 6 && players.every((player) => player.ready);
    const previous = previousLobbyRef.current;

    if (previous) {
      if (ids.size > previous.ids.size) sound.playSfx("player_join");
      if (ids.size < previous.ids.size) sound.playSfx("player_leave");
      const someoneBecameReady = players.some((player) => player.ready && previous.ready.get(player.uid) === false);
      if (someoneBecameReady && !allReady) sound.playSfx("player_ready");
      if (allReady && !previous.allReady) sound.playSfx("all_ready");
    }

    previousLobbyRef.current = { ids, ready, allReady };
  }, [room?.status, players]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    sound.setVoiceDucking(Boolean(room?.settings.voiceSettings.duckBackgroundMusic && voiceChat.someoneSpeaking));
    return () => sound.setVoiceDucking(false);
  }, [room?.settings.voiceSettings.duckBackgroundMusic, sound.setVoiceDucking, voiceChat.someoneSpeaking]);

  const invoke = useCallback(async (name: string, payload: object = {}) => {
    setBusy(true);
    setError(null);
    try {
      return await callRoomCommand(roomId, name, payload as Record<string, unknown>);
    } catch (reason) {
      // Alarm hoặc phiên khác đã chuyển pha: không báo lỗi cho người chơi vì timer cũ đến sau.
      const message = errorMessage(reason);
      if (name !== "advanceIfExpired" || message !== "Pha hành động đã thay đổi.") setError(message);
      throw reason;
    } finally {
      setBusy(false);
    }
  }, [roomId]);

  async function join(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const token = getResumeToken(roomId);
      const result = token
        ? await apiRequest<{ resumeToken: string }>(`/api/rooms/${roomId}/rejoin`, {
          method: "POST",
          body: JSON.stringify({ requestId: requestId(), resumeToken: token }),
        }).catch(() => apiRequest<{ resumeToken: string }>(`/api/rooms/${roomId}/join`, {
          method: "POST",
          body: JSON.stringify({ requestId: requestId(), name: joinName }),
        }))
        : await apiRequest<{ resumeToken: string }>(`/api/rooms/${roomId}/join`, {
          method: "POST",
          body: JSON.stringify({ requestId: requestId(), name: joinName }),
        });
      saveResumeToken(roomId, result.resumeToken);
      localStorage.setItem("ma-soi:name", joinName);
      sound.playSfx("room_join_success", `${roomId}:joined`);
      await new Promise((resolve) => window.setTimeout(resolve, 260));
      window.location.reload();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  async function copyInvite(codeOnly = false) {
    try {
      await navigator.clipboard.writeText(codeOnly ? roomId : window.location.href);
      sound.playSfx("invite_copy");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setError(`Không thể sao chép. Bạn có thể gửi mã phòng ${roomId} cho bạn bè.`);
    }
  }

  async function submitAction(extra: object = {}) {
    if (!room) return;
    await invoke(room.phase === "VOTING" ? "submitVote" : "submitNightAction", {
      gameId: room.gameId,
      phaseId: room.phaseId,
      targetId: selectedTarget,
      ...extra,
    });
    sound.playSfx(room.phase === "VOTING" ? "vote_cast" : "action_confirm", `${roomId}:${room.phaseId}:${userId}:action`);
  }

  async function sendMessage(text: string) {
    await invoke("sendMessage", { channel, text });
  }

  async function hostVoiceCommand(command: string, payload: Record<string, unknown> = {}) {
    await invoke("hostCommand", { command, ...payload });
  }

  async function patchVoiceSettings(settings: Partial<RoomState["settings"]["voiceSettings"]>) {
    await invoke("hostCommand", { command: "voiceSettings", settings });
  }

  const closeMobileSheet = useCallback(() => setMobileSheet(null), []);

  async function leaveRoom() {
    const duringGame = room?.status === "playing";
    const confirmed = window.confirm(
      duringGame
        ? "Bạn muốn thoát khỏi ván đang diễn ra? Ghế và vai của bạn sẽ được giữ để ván chơi không bị gián đoạn."
        : "Bạn muốn rời khỏi phòng này?",
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      const result = await callRoomCommand<{ ok: true; seatRetained?: boolean }>(roomId, "leaveRoom");
      removeResumeToken(roomId);
      navigate("/", {
        replace: true,
        state: {
          notice: result.seatRetained
            ? "Bạn đã thoát khỏi ván. Ghế và vai vẫn được giữ cho đến khi ván kết thúc."
            : "Bạn đã rời phòng.",
        },
      });
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  async function cancelRoom() {
    const confirmed = window.confirm(
      room?.status === "playing"
        ? "Hủy phòng ngay bây giờ? Ván chơi sẽ kết thúc và tất cả thành viên bị đưa về trang chủ."
        : "Hủy phòng này? Tất cả thành viên sẽ bị đưa về trang chủ.",
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await callRoomCommand(roomId, "cancelRoom");
      removeResumeToken(roomId);
      navigate("/", {
        replace: true,
        state: { notice: "Bạn đã hủy phòng." },
      });
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  if (connection.needsJoin) {
    return (
      <div className="direct-join-page">
        <AnimatedLandingBackground variant="compact" />
        <SurfacePanel variant="tavern" className={`direct-join-panel ${error ? "join-panel-error" : ""}`}>
          <form onSubmit={join} className="room-form">
            <DoorOpen className="mx-auto size-10 text-red-300" />
            <div className="panel-heading text-center">
              <p className="cinematic-eyebrow">Lời mời xuyên qua màn sương</p>
              <h1>Bước vào ngôi làng</h1>
              <span className="panel-divider" />
            </div>
            <div className="engraved-code-plate">
              <span>Mã ngôi làng</span>
              <strong>{roomId}</strong>
            </div>
            <label className="field-label">
              Tên của bạn trong làng
              <input className="field-input" value={joinName} onChange={(e) => setJoinName(e.target.value)} minLength={2} maxLength={20} required />
            </label>
            {error && <p className="error-box">{error}</p>}
            <GameButton
              type="submit"
              fullWidth
              disabled={busy}
              icon={<DoorOpen className="size-5" />}
              onMouseEnter={() => sound.playSfx("ui_hover")}
            >
              {busy ? "Sương đang mở lối..." : "Bước vào làng"}
            </GameButton>
          </form>
        </SurfacePanel>
      </div>
    );
  }

  if (!room || !currentPlayer) {
    return (
      <div className="direct-join-page">
        <AnimatedLandingBackground variant="compact" />
        <SurfacePanel className="loading-mist-card">
          <div role="status" aria-live="polite">
            <span className="phase-orbit" aria-hidden="true" />
            <p className="cinematic-eyebrow">Đang vào làng</p>
            <h1>Đồng bộ cuộc chơi…</h1>
            <p>Trạng thái hiện tại đang được phục hồi an toàn.</p>
          </div>
          <span className="loading-skeleton wide" aria-hidden="true" />
          <span className="loading-skeleton" aria-hidden="true" />
        </SurfacePanel>
      </div>
    );
  }

  if (room.status === "lobby") {
    const allPlayersReady = players.length >= 6 && players.every((player) => player.ready);
    const canStart = isHost && allPlayersReady;
    const inviteHref = window.location.href;

    return (
      <>
      <VoiceAudioSink voice={voiceChat} />
      <div className="lobby-page gothic-lobby">
        {!connection.connected && <div className="reconnect-banner" role="status"><strong>Đang kết nối lại…</strong><span>Các ghế vẫn được giữ. Vui lòng chờ kết nối phục hồi.</span></div>}
        <section className="lobby-cinematic-header">
          <div className="lobby-title-copy">
            <p className="cinematic-eyebrow">Sảnh chờ · {room.settings.visibility === "public" ? "Công khai" : "Riêng tư"}</p>
            <h1>{room.settings.title}</h1>
            <p>Dân làng đang tụ họp trước sân hội đồng. Khi nến đã sáng đủ, đêm trăng máu bắt đầu.</p>
          </div>
          <div className="lobby-header-actions">
            <RoomSeal code={roomId} copied={copied} onCopy={() => void copyInvite(true)} />
            {isHost && (
              <GameButton
                type="button"
                variant="secondary"
                small
                disabled={busy}
                icon={<Settings2 className="size-4" />}
                onMouseEnter={() => sound.playSfx("ui_hover")}
                aria-haspopup="dialog"
                aria-expanded={showLobbySettings}
                onClick={() => setShowLobbySettings(true)}
              >
                {showLobbySettings ? "Đóng cài đặt" : "Cài đặt phòng"}
              </GameButton>
            )}
            <GameButton
              type="button"
              variant="danger"
              small
              disabled={busy}
              icon={isHost ? <Trash2 className="size-4" /> : <LogOut className="size-4" />}
              onMouseEnter={() => sound.playSfx("ui_hover")}
              onClick={() => void (isHost ? cancelRoom() : leaveRoom())}
            >
              {isHost ? "Giải tán làng" : "Rời khỏi làng"}
            </GameButton>
          </div>
        </section>

        {(error || connection.error) && <p className="error-box mb-4">{error ?? connection.error}</p>}

        <div className="lobby-council-layout">
          <LobbyCouncilBoard title="Sảnh hội đồng" count={players.length} max={room.settings.maxPlayers}>
            <InviteScroll href={inviteHref} copied={copied} onCopy={() => void copyInvite()} />
            <div className="player-lobby-grid">
              {Array.from({ length: room.settings.maxPlayers }, (_, index) => {
                const player = players.find((candidate) => candidate.seat === index + 1);
                return player
                  ? <PlayerLobbyCard key={player.uid} player={player} voice={voiceByPlayer.get(player.uid)} index={index} isSelf={player.uid === userId} onChangeAvatar={() => setShowAvatarPicker(true)} avatarDisabled={busy || !connection.connected} />
                  : <EmptySeatCard key={`empty-${index}`} index={index + 1} />;
              })}
            </div>
            <div className="lobby-action-bar">
            <p className="lobby-ready-summary" role="status">{players.filter((player) => player.ready).length}/{players.length} người sẵn sàng{players.length < 6 ? ` · Cần thêm ${6 - players.length} người` : ""}</p>
            <div className="lobby-primary-actions">
              <GameButton
                type="button"
                variant={currentPlayer.ready ? "secondary" : "gold"}
                fullWidth
                icon={<ShieldCheck className="size-5" />}
                onMouseEnter={() => sound.playSfx("ui_hover")}
                onClick={() => {
                  sound.playSfx("ui_click");
                  void invoke("setReady", { ready: !currentPlayer.ready }).catch(() => undefined);
                }}
                disabled={busy || !connection.connected}
              >
                {currentPlayer.ready ? "Hủy sẵn sàng" : "Sẵn sàng"}
              </GameButton>
              {isHost && (
                <GameButton
                  type="button"
                  className={`start-ritual-banner ${allPlayersReady ? "all-ready" : ""}`}
                  fullWidth
                  dramatic
                  icon={<Sparkles className="size-5" />}
                  onMouseEnter={() => sound.playSfx("ui_hover")}
                  onClick={() => {
                    sound.playSfx("ui_click");
                    void invoke("startGame").catch(() => undefined);
                  }}
                  disabled={busy || !canStart || !connection.connected}
                >
                  Bắt đầu ván
                </GameButton>
              )}
            </div>
            {isHost && players.length >= 6 && !allPlayersReady && <p className="lobby-hint">Chờ tất cả dân làng sẵn sàng.</p>}
            {!isHost && <p className="lobby-hint">Chủ phòng sẽ bắt đầu khi mọi người sẵn sàng.</p>}
            <nav className="lobby-mobile-tools" aria-label="Công cụ sảnh">
              <button type="button" onClick={() => setMobileSheet("voice")} aria-haspopup="dialog"><Headphones />{voiceChat.canSendAudio ? "Mic đang bật" : voiceChat.voiceEnabled ? "Đang nghe" : "Âm thanh"}</button>
              <button type="button" onClick={() => setMobileSheet("chat")} aria-haspopup="dialog"><MessageCircle />Trò chuyện</button>
            </nav>
            </div>
          </LobbyCouncilBoard>

          <aside className="lobby-side-stack">
            <details className="room-settings-summary surface-panel"><summary>Thiết lập phòng · {room.settings.maxPlayers} ghế</summary>
              <dl>
                <div>
                  <dt>Số dân làng</dt>
                  <dd>{players.length}/{room.settings.maxPlayers}</dd>
                </div>
                <div>
                  <dt>Tranh luận</dt>
                  <dd>{room.settings.discussionSeconds / 60} phút</dd>
                </div>
                <div>
                  <dt>Bỏ phiếu</dt>
                  <dd>{room.settings.votingSeconds} giây</dd>
                </div>
                <div>
                  <dt>Hành động đêm</dt>
                  <dd>{room.settings.nightActionSeconds} giây</dd>
                </div>
                <div>
                  <dt>Kênh thoại Ma Sói</dt>
                  <dd>{room.settings.voiceSettings.werewolfNightVoice ? "Mở" : "Đóng"}</dd>
                </div>
                <div>
                  <dt>Tiết lộ vai</dt>
                  <dd>{room.settings.revealRoleOnDeath ? "Khi chết" : "Giữ bí mật"}</dd>
                </div>
              </dl>
              <div className="role-summary-runes">
                <span>{room.settings.roleCounts.WEREWOLF} Sói</span>
                <span>{room.settings.roleCounts.SEER} Tiên tri</span>
                <span>{room.settings.roleCounts.WITCH} Phù thủy</span>
                <span>{room.settings.roleCounts.GUARD} Bảo vệ</span>
                <span>{room.settings.roleCounts.HUNTER} Thợ săn</span>
                <span>{room.settings.roleCounts.VILLAGER} Dân làng</span>
              </div>
            </details>
            <div className="lobby-desktop-voice"><VoicePanel
              voice={voiceChat}
              settings={room.settings.voiceSettings}
              players={players}
              backgroundVolume={sound.backgroundVolume}
              setBackgroundVolume={sound.setBackgroundVolume}
              sfxVolume={sound.sfxVolume}
              setSfxVolume={sound.setSfxVolume}
            /></div>
            <div className="lobby-desktop-chat"><ChatPanel messages={messages} channel="lobby" canSend={!currentPlayer.muted && connection.connected} onSend={sendMessage} players={players} /></div>
          </aside>
        </div>
        {showAvatarPicker && <AvatarPicker player={currentPlayer} connected={connection.connected} onClose={() => setShowAvatarPicker(false)} onSave={(avatarId) => invoke("setAvatar", { avatarId })} />}
        {isHost && (
          <BottomSheet open={showLobbySettings} title="Cài đặt phòng" onClose={() => setShowLobbySettings(false)}>
          {error && <p className="error-box" role="alert">{error}</p>}
          <LobbySettings
            settings={room.settings}
            playerCount={players.length}
            busy={busy}
            onCancel={() => setShowLobbySettings(false)}
            onSave={async (settings) => {
              await invoke("updateRoomSettings", settings);
              setShowLobbySettings(false);
            }}
          />
          <HostVoiceControls players={players} settings={room.settings.voiceSettings} hostMutedPlayerIds={voiceSnapshot?.hostMutedPlayerIds ?? []} busy={busy} onCommand={hostVoiceCommand} onSettingsPatch={patchVoiceSettings} />
          </BottomSheet>
        )}

        <BottomSheet open={mobileSheet === "voice"} title="Âm thanh trong làng" onClose={closeMobileSheet}>
          <VoicePanel voice={voiceChat} settings={room.settings.voiceSettings} players={players} backgroundVolume={sound.backgroundVolume} setBackgroundVolume={sound.setBackgroundVolume} sfxVolume={sound.sfxVolume} setSfxVolume={sound.setSfxVolume} />
        </BottomSheet>
        <BottomSheet open={mobileSheet === "chat"} title="Trò chuyện trong sảnh" onClose={closeMobileSheet}>
          <ChatPanel messages={messages} channel="lobby" canSend={!currentPlayer.muted && connection.connected} onSend={sendMessage} players={players} />
        </BottomSheet>
      </div>
      </>
    );
  }

  return (
    <>
    <VoiceAudioSink voice={voiceChat} />
    <div className={`game-page game-phase-${room.phase.toLowerCase().replaceAll("_", "-")}`}>
      {!connection.connected && (
        <div className="reconnect-banner" role="status">
          <span className="connection-pulse" aria-hidden="true" />
          <strong>Đang kết nối lại…</strong>
          <span>Trạng thái gần nhất vẫn được giữ trên màn hình.</span>
        </div>
      )}
      <div className="phase-hero mb-5">
        {visual.pulse && <div className="phase-vfx" key={visual.pulse.key} data-event-key={visual.pulse.key} aria-hidden="true"><ClawMark /></div>}
        <div className="phase-heading">
          {room.phase !== "ROLE_REVEAL" && <PhaseSigil phase={room.phase} />}
          <div>
          <p className="eyebrow">{room.phase === "ROLE_REVEAL" ? `Phòng ${roomId}` : `${room.phase.startsWith("NIGHT_") ? "Đêm" : "Ngày"} ${room.dayNumber} · ${players.filter((player) => player.alive).length} người còn sống`}</p>
            <div key={`${room.gameId}:${room.phaseId}`} className={visual.enter ? "phase-title is-entering" : "phase-title"}>
              <h1 className="font-display text-3xl text-amber-50 sm:text-4xl">{PHASE_COPY[room.phase].title}</h1>
              <p className="mt-1 text-sm italic text-stone-400">{room.phase === "ROLE_REVEAL" ? "Mở lá bài và ghi nhớ vai của bạn." : PHASE_COPY[room.phase].narration}</p>
            </div>
          </div>
        </div>
        <div className="room-toolbar">
          <Timer
            endsAtMs={room.phaseEndsAtMs}
            paused={room.paused}
            onUrgentTick={(seconds) => sound.playSfx("countdown_tick", `${roomId}:${room.phaseId}:tick:${seconds}`)}
            onExpired={() => {
              sound.playSfx("timer_expired", `${roomId}:${room.phaseId}:expired`);
              void invoke("advanceIfExpired", { gameId: room.gameId, phaseId: room.phaseId }).catch(() => undefined);
            }}
          />
          {isHost && (
            <button className="icon-button" onClick={() => setShowHost((value) => !value)} aria-label="Điều khiển chủ phòng">
              <Settings2 className="size-5" />
            </button>
          )}
          {room.phase !== "ENDED" && (
            <button
              className="danger-button small"
              disabled={busy}
              onClick={() => void (isHost ? cancelRoom() : leaveRoom())}
            >
              {isHost ? <Trash2 className="size-4" /> : <LogOut className="size-4" />}
              {isHost ? "Hủy phòng" : "Thoát phòng"}
            </button>
          )}
        </div>
      </div>

      {showHost && isHost && (
        <div className="mb-5 grid gap-4 lg:grid-cols-[1fr_360px]">
          <div className="surface-panel flex flex-wrap items-center gap-2 p-3">
            <span className="mr-auto text-xs uppercase tracking-wider text-stone-500">Điều khiển chủ phòng · mọi thao tác đều được ghi lại</span>
            <button className="ghost-button small" onClick={() => void invoke("hostCommand", { command: room.paused ? "resume" : "pause" })}>
              {room.paused ? <Play className="size-4" /> : <Pause className="size-4" />}
              {room.paused ? "Tiếp tục" : "Tạm dừng"}
            </button>
            <button className="ghost-button small" onClick={() => void invoke("hostCommand", { command: "skip" })}>
              <SkipForward className="size-4" /> Bỏ qua pha
            </button>
            <button className="ghost-button small" onClick={() => void invoke("hostCommand", { command: "restart" })}>
              <RotateCcw className="size-4" /> Chơi lại
            </button>
          </div>
          <HostVoiceControls
            players={players}
            settings={room.settings.voiceSettings}
            hostMutedPlayerIds={voiceSnapshot?.hostMutedPlayerIds ?? []}
            busy={busy}
            onCommand={hostVoiceCommand}
            onSettingsPatch={patchVoiceSettings}
          />
        </div>
      )}

      {(error || connection.error) && <p className="error-box mb-4">{error ?? connection.error}</p>}

      <div className={`game-voice-dock ${room.phase === "NIGHT_WOLVES" && privatePlayer?.role === "WEREWOLF" ? "wolf-night-strip" : ""}`}>
        <VoicePanel
          voice={voiceChat}
          settings={room.settings.voiceSettings}
          players={players}
          backgroundVolume={sound.backgroundVolume}
          setBackgroundVolume={sound.setBackgroundVolume}
          sfxVolume={sound.sfxVolume}
          setSfxVolume={sound.setSfxVolume}
        />
      </div>

      {room.phase === "ROLE_REVEAL" ? (
        <RoleReveal
          key={`${roomId}:${room.gameId}:${currentPlayer.uid}`}
          guardRepeatTarget={room.settings.guardRepeatTarget}
          connected={connection.connected}
          privatePlayer={privatePlayer}
          currentPlayer={currentPlayer}
          players={players}
          busy={busy}
          onAcknowledge={() => invoke("acknowledgeRole", { gameId: room.gameId, phaseId: room.phaseId })}
        />
      ) : room.phase === "VOTE_RESULT" ? (
        <div className="game-main-layout vote-result-layout">
          <VoteResultView room={room} players={players} striking={Boolean(visual.pulse)} />
          <aside className="game-side-rail">
            <ChatPanel messages={messages} channel={channel} players={players}
              canSend={!currentPlayer.muted && channel === "ghost"}
              readOnlyReason="Chat tạm khóa khi bỏ phiếu" onSend={sendMessage} />
          </aside>
        </div>
      ) : room.phase === "ENDED" ? (
        <EndGame
          room={room}
          players={players}
          voiceParticipants={voiceParticipants}
          isHost={isHost}
          busy={busy}
          onRestart={() => invoke("hostCommand", { command: "restart" })}
          onClose={isHost ? cancelRoom : leaveRoom}
        />
      ) : (
        <div className="game-main-layout">
          <div className="game-primary-column">
            {privatePlayer?.role === "SEER" && privatePlayer.seerHistory.length > 0 && (
              <SeerHistoryPanel privatePlayer={privatePlayer} />
            )}
            <ActionPanel
              key={`${room.gameId}:${room.phaseId}:${currentPlayer.uid}`}
              room={room}
              players={players}
              currentPlayer={currentPlayer}
              privatePlayer={privatePlayer}
              selectedTarget={selectedTarget}
              setSelectedTarget={(targetId) => {
                if (targetId && targetId !== selectedTarget) sound.playSfx("target_select");
                setSelectedTarget(targetId);
              }}
              busy={busy}
              connected={connection.connected}
              strikingIds={visual.pulse?.playerIds}
              onSubmit={submitAction}
            />
            <section className="surface-panel roster-panel">
              <h2 className="panel-title mb-4">Người trong làng</h2>
              <PlayerGrid players={players} voiceParticipants={voiceParticipants} />
            </section>
          </div>
          <aside className="game-side-rail">
            <ChatPanel
              messages={messages}
              channel={channel}
              players={players}
              canSend={!currentPlayer.muted && (
                channel === "ghost"
                || channel === "wolves"
                || (channel === "alive" && room.phase === "DISCUSSION" && currentPlayer.alive)
              )}
              readOnlyReason={room.phase === "VOTING" ? "Chat tạm khóa khi bỏ phiếu" : undefined}
              onSend={sendMessage}
            />
            <section className="surface-panel p-4">
              <h3 className="panel-title mb-3">Biên niên sử</h3>
              <div className="max-h-64 space-y-3 overflow-y-auto text-sm">
                {events.slice(-12).reverse().map((entry) => (
                  <p key={entry.id} className="border-l border-red-900/70 pl-3 text-stone-400">{entry.message}</p>
                ))}
              </div>
            </section>
          </aside>
        </div>
      )}

      {room.phase !== "ENDED" && room.phase !== "ROLE_REVEAL" && (
        <>
          <MobileGameDock
            active={mobileSheet}
            isHost={Boolean(isHost)}
            voiceLabel={voiceSnapshot?.access.reason === "voting-muted" ? "Mic tắt" : voiceSnapshot?.access.canSpeak ? "Mic" : "Nghe"}
            onOpen={setMobileSheet}
          />
          <BottomSheet open={mobileSheet === "voice"} title="Âm thanh trong làng" onClose={closeMobileSheet}>
            <VoicePanel
              voice={voiceChat}
              settings={room.settings.voiceSettings}
              players={players}
              backgroundVolume={sound.backgroundVolume}
              setBackgroundVolume={sound.setBackgroundVolume}
              sfxVolume={sound.sfxVolume}
              setSfxVolume={sound.setSfxVolume}
            />
          </BottomSheet>
          <BottomSheet open={mobileSheet === "chat"} title="Chat" onClose={closeMobileSheet}>
            <ChatPanel
              messages={messages}
              channel={channel}
              players={players}
              canSend={!currentPlayer.muted && (
                channel === "ghost"
                || channel === "wolves"
                || (channel === "alive" && room.phase === "DISCUSSION" && currentPlayer.alive)
              )}
              readOnlyReason={room.phase === "VOTING" || room.phase === "VOTE_RESULT" ? "Chat tạm khóa khi bỏ phiếu" : undefined}
              onSend={sendMessage}
            />
          </BottomSheet>
          <BottomSheet open={mobileSheet === "chronicle"} title="Biên niên sử" onClose={closeMobileSheet}>
            <div className="chronicle-list">
              {events.length === 0 && <p>Chưa có sự kiện nào được ghi lại.</p>}
              {events.slice(-20).reverse().map((entry) => <p key={entry.id}>{entry.message}</p>)}
            </div>
          </BottomSheet>
          {isHost && (
            <BottomSheet open={mobileSheet === "host"} title="Điều khiển chủ phòng" onClose={closeMobileSheet}>
              <div className="mobile-host-actions">
                <button className="ghost-button" onClick={() => void invoke("hostCommand", { command: room.paused ? "resume" : "pause" })}>
                  {room.paused ? <Play /> : <Pause />}{room.paused ? "Tiếp tục" : "Tạm dừng"}
                </button>
                <button className="ghost-button" onClick={() => void invoke("hostCommand", { command: "skip" })}>
                  <SkipForward />Bỏ qua pha
                </button>
              </div>
              <HostVoiceControls
                players={players}
                settings={room.settings.voiceSettings}
                hostMutedPlayerIds={voiceSnapshot?.hostMutedPlayerIds ?? []}
                busy={busy}
                onCommand={hostVoiceCommand}
                onSettingsPatch={patchVoiceSettings}
              />
            </BottomSheet>
          )}
        </>
      )}
    </div>
    </>
  );
}

type LobbySettingsDraft = {
  title: string;
  visibility: "public" | "private";
  maxPlayers: number;
  discussionSeconds: number;
  votingSeconds: number;
  nightActionSeconds: number;
  revealRoleOnDeath: boolean;
  ghostChatEnabled: boolean;
  guardRepeatTarget: boolean;
  disabledSpecialRole: "" | "SEER" | "WITCH" | "HUNTER" | "GUARD";
};

function LobbySettings({
  settings,
  playerCount,
  busy,
  onCancel,
  onSave,
}: {
  settings: RoomState["settings"];
  playerCount: number;
  busy: boolean;
  onCancel: () => void;
  onSave: (settings: Omit<LobbySettingsDraft, "disabledSpecialRole"> & {
    disabledSpecialRole: null | "SEER" | "WITCH" | "HUNTER" | "GUARD";
  }) => Promise<void>;
}) {
  const [draft, setDraft] = useState<LobbySettingsDraft>(() => ({
    title: settings.title,
    visibility: settings.visibility,
    maxPlayers: settings.maxPlayers,
    discussionSeconds: settings.discussionSeconds,
    votingSeconds: settings.votingSeconds,
    nightActionSeconds: settings.nightActionSeconds,
    revealRoleOnDeath: settings.revealRoleOnDeath,
    ghostChatEnabled: settings.ghostChatEnabled,
    guardRepeatTarget: settings.guardRepeatTarget,
    disabledSpecialRole: settings.disabledSpecialRole ?? "",
  }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await onSave({
        ...draft,
        disabledSpecialRole: draft.disabledSpecialRole || null,
      });
    } catch {
      // Lỗi đã được hiển thị bởi RoomPage.
    }
  }

  const maxPlayerOptions = Array.from(
    new Set([
      ...Array.from({ length: 11 }, (_, index) => index + 6),
      draft.maxPlayers,
      playerCount,
    ]),
  ).filter((count) => count >= 6 && count <= 16).sort((left, right) => left - right);

  return (
    <SurfacePanel variant="ritual" className="lobby-settings-panel mb-5">
    <form onSubmit={submit} className="room-form lobby-settings-form">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="cinematic-eyebrow">Dành cho chủ phòng</p>
          <h2 className="font-display text-2xl text-[#2a130d]">Thiết lập lời nguyền</h2>
          <p className="mt-1 text-sm text-[#6d3f2c]">
            Khi lưu thay đổi, mọi người sẽ cần xác nhận sẵn sàng lại.
          </p>
        </div>
        <span className="role-chip">{playerCount}/{draft.maxPlayers} người</span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="field-label sm:col-span-2">
          Tên ngôi làng
          <input
            className="field-input"
            value={draft.title}
            onChange={(event) => setDraft((value) => ({ ...value, title: event.target.value }))}
            minLength={2}
            maxLength={50}
            required
          />
        </label>
        <label className="field-label">
          Quyền riêng tư
          <select
            className="field-input"
            value={draft.visibility}
            onChange={(event) => setDraft((value) => ({
              ...value,
              visibility: event.target.value as LobbySettingsDraft["visibility"],
            }))}
          >
            <option value="private">Phòng riêng</option>
            <option value="public">Phòng công khai</option>
          </select>
        </label>
        <label className="field-label">
          Sức chứa
          <select
            className="field-input"
            value={draft.maxPlayers}
            onChange={(event) => setDraft((value) => ({
              ...value,
              maxPlayers: Number(event.target.value),
            }))}
          >
            {maxPlayerOptions.map((count) => (
              <option key={count} value={count} disabled={count < playerCount}>
                {count} người{count < playerCount ? " · đang có nhiều hơn" : ""}
              </option>
            ))}
          </select>
        </label>

        <label className="field-label">
          Thảo luận
          <select
            className="field-input"
            value={draft.discussionSeconds}
            onChange={(event) => setDraft((value) => ({
              ...value,
              discussionSeconds: Number(event.target.value),
            }))}
          >
            {[60, 120, 180, 300, 600].map((seconds) => (
              <option key={seconds} value={seconds}>{seconds / 60} phút</option>
            ))}
          </select>
        </label>
        <label className="field-label">
          Bỏ phiếu
          <select
            className="field-input"
            value={draft.votingSeconds}
            onChange={(event) => setDraft((value) => ({
              ...value,
              votingSeconds: Number(event.target.value),
            }))}
          >
            {[30, 45, 60, 90, 120].map((seconds) => (
              <option key={seconds} value={seconds}>{seconds} giây</option>
            ))}
          </select>
        </label>
        <label className="field-label">
          Hành động đêm
          <select
            className="field-input"
            value={draft.nightActionSeconds}
            onChange={(event) => setDraft((value) => ({
              ...value,
              nightActionSeconds: Number(event.target.value),
            }))}
          >
            {[30, 45, 60, 75, 90].map((seconds) => (
              <option key={seconds} value={seconds}>{seconds} giây</option>
            ))}
          </select>
        </label>
        <label className="field-label">
          Bộ vai
          <select
            className="field-input"
            value={draft.disabledSpecialRole}
            onChange={(event) => setDraft((value) => ({
              ...value,
              disabledSpecialRole: event.target.value as LobbySettingsDraft["disabledSpecialRole"],
            }))}
          >
            <option value="">Giữ bộ mặc định</option>
            <option value="SEER">Không có Tiên tri</option>
            <option value="WITCH">Không có Phù thủy</option>
            <option value="HUNTER">Không có Thợ săn</option>
            <option value="GUARD">Không có Bảo vệ</option>
          </select>
        </label>
      </div>

      <div className="settings-rune-grid mt-5">
        <SettingsToggle
          checked={draft.revealRoleOnDeath}
          onChange={(checked) => setDraft((value) => ({ ...value, revealRoleOnDeath: checked }))}
          title="Công bố vai khi chết"
          description="Cho cả làng biết vai của người bị loại."
        />
        <SettingsToggle
          checked={draft.ghostChatEnabled}
          onChange={(checked) => setDraft((value) => ({ ...value, ghostChatEnabled: checked }))}
          title="Chat cõi chết"
          description="Người đã chết vẫn trò chuyện riêng với nhau."
        />
        <SettingsToggle
          checked={draft.guardRepeatTarget}
          onChange={(checked) => setDraft((value) => ({ ...value, guardRepeatTarget: checked }))}
          title="Bảo vệ liên tiếp"
          description="Bảo vệ được chọn cùng một người nhiều đêm."
        />
      </div>

      <div className="mt-5 flex flex-col justify-end gap-3 sm:flex-row">
        <GameButton type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Hủy thay đổi
        </GameButton>
        <GameButton type="submit" variant="gold" disabled={busy} icon={<Settings2 className="size-4" />}>
          {busy ? "Đang lưu…" : "Lưu cài đặt"}
        </GameButton>
      </div>
    </form>
    </SurfacePanel>
  );
}

function SeerHistoryPanel({ privatePlayer }: { privatePlayer: PrivatePlayer }) {
  const latest = privatePlayer.seerHistory.at(-1)!;

  return (
    <section
      key={`${latest.day}-${latest.targetId}`}
      className={`surface-panel seer-result-panel ${latest.isWerewolf ? "seer-result-wolf" : "seer-result-safe"}`}
      aria-live="assertive"
    >
      <div className="seer-result-header">
        <span className="seer-result-sigil"><Sparkles /></span>
        <div>
          <p className="eyebrow">Chỉ bạn nhìn thấy · Tiên tri</p>
          <h2>Kết quả soi mới nhất</h2>
        </div>
        <span className="seer-result-night">Đêm {latest.day}</span>
      </div>
      <div className="seer-result-revelation">
        <span className="seer-result-target-label">Người được soi</span>
        <strong className="seer-result-target">{latest.targetName}</strong>
        <span className="seer-result-divider" aria-hidden="true" />
        <strong className="seer-result-verdict">
          {latest.isWerewolf ? <Skull /> : <ShieldCheck />}
          {latest.isWerewolf ? "Sói" : "Không phải Sói"}
        </strong>
      </div>
      {privatePlayer.seerHistory.length > 1 && (
        <div className="seer-history-list">
          <p>Những điều bạn đã thấy trước đó</p>
          {privatePlayer.seerHistory.slice(0, -1).map((entry) => (
            <div key={`${entry.day}-${entry.targetId}`} className="seer-history-entry">
              <span>Đêm {entry.day}</span>
              <strong>{entry.targetName}</strong>
              <em className={entry.isWerewolf ? "is-wolf" : "is-safe"}>
                {entry.isWerewolf ? "Sói" : "Không phải Sói"}
              </em>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function EndGame({
  room,
  players,
  voiceParticipants,
  isHost,
  busy,
  onRestart,
  onClose,
}: {
  room: RoomState;
  players: PublicPlayer[];
  voiceParticipants: VoiceParticipant[];
  isHost: boolean;
  busy: boolean;
  onRestart: () => Promise<unknown>;
  onClose: () => Promise<void>;
}) {
  const villagersWon = room.winner === "VILLAGERS";
  return (
    <section className={`surface-panel endgame-panel gothic-frame ${villagersWon ? "villagers-win" : "wolves-win"}`}>
      <PhaseSigil phase={villagersWon ? "MORNING" : "NIGHT_WOLVES"} />
      <p className="eyebrow mt-4">Ván chơi đã kết thúc</p>
      <h1 className={`font-display mt-2 text-4xl sm:text-5xl ${villagersWon ? "text-amber-100" : "text-red-400"}`}>
        {villagersWon ? "Dân làng sống sót" : "Ma sói thống trị"}
      </h1>
      <p className="mx-auto mt-4 max-w-xl text-stone-400">
        {villagersWon ? "Bình minh đã xé tan màn đêm trăng máu. Những người còn lại sẽ nhớ mãi cái giá của sự thật." : "Tiếng hú phủ kín mái nhà. Ngôi làng không còn ai để thắp ngọn nến cuối cùng."}
      </p>
      <div className="mt-8">
        <PlayerGrid players={players} voiceParticipants={voiceParticipants} />
      </div>
      <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
        {isHost && <button className="gold-button" disabled={busy} onClick={() => void onRestart().catch(() => undefined)}><RotateCcw className="size-4" /> Chơi lại</button>}
        <button className="danger-button" disabled={busy} onClick={() => void onClose()}>
          {isHost ? <Trash2 className="size-4" /> : <LogOut className="size-4" />}
          {isHost ? "Hủy phòng" : "Thoát phòng"}
        </button>
      </div>
    </section>
  );
}
